<template>
  <modal-wrap
    :id="modalName"
    :name="modalName"
    :header-class="'border-0 fix-error-header'"
    :body-class="'fix-error-modal assignment-form'"
    :hide-footer="true"
    size="md"
  >
    <template #modal-title>
      <h4 class="mb-0">Assignment validation errors list</h4>
    </template>
    <template #default>
      <div class="row">
        <div class="col-12 col-md-6">
          <input id="fatal_level" v-model="level" name="level" type="radio" value="fatal" checked />
          <label for="fatal_level" class="radio-group">
            <span class="radio-title"> Fatal only </span>
          </label>
        </div>
        <div class="col-12 col-md-6">
          <input id="all_level" v-model="level" name="level" type="radio" value="all" />
          <label for="all_level" class="radio-group">
            <span class="radio-title"> All </span>
          </label>
        </div>
      </div>
      <div v-if="loading" class="text-center">
        <b-spinner label="Loading..." />
      </div>
      <div v-else class="col-12">
        <div class="easy-data-table-dark">
          <VeeForm v-slot="{ values }" @submit="submit">
            <easy-data-table
              ref="dataTableErrors"
              table-class-name="vue3-easy-data-table table-shadow"
              :server-items-length="serverItemsLength"
              :loading="loading"
              :headers="headers"
              :items="items"
              :prevent-context-menu-row="false"
              :fixed-header="false"
              no-hover
              alternating
              hide-footer
            >
              <template #loading>
                <b-spinner class="mt-5" variant="success" />
              </template>
              <template #no-data>
                <div style="text-align: center">No data available</div>
              </template>
              <template #body>
                <tbody class="vue3-easy-data-table__body row-alternation">
                  <tr v-for="(item, key) in items" :key="key">
                    <td v-if="!item.flagId" colspan="5" class="center-text p-1">
                      <b>{{ item.itemReference }}: {{ item.itemTitle }}</b>
                    </td>
                    <td v-for="(header, hKey) in headers" v-else :key="hKey" class="p-1">
                      <div v-if="header.value === 'value'">
                        <span v-b-tooltip="{ title: item.reason, variant: 'info' }">
                          {{ item.value ? item.value : '-' }}
                        </span>
                      </div>
                      <div v-else-if="header.value === 'arrow'">
                        <i class="fa fa-arrow-right" />
                      </div>
                      <div v-else-if="header.value === 'newValue'">
                        <Field
                          v-slot="{ field, errors, meta }"
                          :model-value="item.newValue"
                          :name="item.flagId + '|' + item.field"
                          :rules="{ simpleRequired: values[`${item.flagId}|apply`] }"
                        >
                          <span
                            v-b-tooltip="{
                              title: errors[0] ?? item.newReason ?? 'Please set a value.',
                              variant: 'info',
                            }"
                          >
                            <input v-bind="field" type="text" :class="[getValidationState(meta, errors)]" />
                          </span>
                        </Field>
                      </div>
                      <div v-else-if="header.value === 'field'">
                        <b>{{ normalizeName(item.field) }}</b>
                      </div>
                      <div v-else-if="header.value === 'apply'">
                        <Field
                          v-slot="{ field }"
                          type="checkbox"
                          :value="true"
                          :model-value="true"
                          :unchecked-value="false"
                          :name="item.flagId + '|apply'"
                        >
                          <div class="form-check">
                            <input v-bind="field" type="checkbox" class="form-check-input" />
                          </div>
                        </Field>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </easy-data-table>
            <div class="form-group mt-4 mb-0">
              <button type="submit" class="btn create-btn btn-green">Update checked</button>
            </div>
          </VeeForm>
        </div>
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { Assignment, ValidationErrorSuggestion, ValidationFixes } from '@/types/Models/Assignment'
import { BSpinner } from 'bootstrap-vue-next'
import { Form as VeeForm, Field } from 'vee-validate'
import { defineComponent, watch, ref, computed, type ComputedRef, type Ref } from 'vue'
import { FIX_ERRORS_MODAL } from '@/const/ModalTypes'
import { catchErrors } from '@/services/Helpers'
import { registerModals } from '@/services/Modal'
import { normalizeName, toCamelCase } from '@/services/Utils'
import { getValidationState } from '@/services/Validation'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { useVueNextModalStore } from '@/store/vueNextModal'
import EasyDataTable, { type Header } from 'vue3-easy-data-table'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'FixErrorsModal',
  components: { BSpinner, EasyDataTable, ModalWrap, VeeForm, Field },
  setup() {
    const modalStore = useVueNextModalStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()
    const modalName = FIX_ERRORS_MODAL

    const headers: Header[] = [
      { text: 'Field', value: 'field' },
      { text: 'Old value', value: 'value' },
      { text: '', value: 'arrow' },
      { text: 'New value', value: 'newValue' },
      { text: '', value: 'apply' },
    ]

    const dataTableErrors: Ref = ref()
    const loading: Ref<boolean> = ref(false)
    const level: Ref<string> = ref('fatal')
    const payload: Ref<ValidationFixes> = ref({ fixes: [] })

    const assignment: ComputedRef<Assignment | null> = computed(() => assignmentSummaryStore.assignment)
    const items: ComputedRef<ValidationErrorSuggestion[]> = computed(() => assignmentSummaryStore.getValidationErrors)
    const serverItemsLength: ComputedRef<number> = computed(() => items.value.length)

    const reloadErrors = () => {
      if (assignment.value && assignment.value?.totalErrorsCount > 0) {
        loading.value = true
        assignmentSummaryStore.fetchValidationErrors(level.value)?.finally(() => {
          loading.value = false
        })
      } else {
        assignmentSummaryStore.clearValidationErrors()
      }
    }
    const submit = (val: any) => {
      const availableFlags = [
        'weight',
        'width',
        'height',
        'length',
        'city',
        'email',
        'last_name',
        'first_name',
        'postal_code',
        'country_code',
        'address_line',
        'phone_number',
        'description',
        'commodity_code',
        'estimated_cost',
      ]
      const fixes: any = {}
      Object.keys(val).forEach((key) => {
        const item = key.split('|')
        const flagId = item[0] || ''
        const field = item[1] || ''
        const itemId = items.value.find((i) => i.flagId === flagId)?.itemId || ''

        if (!itemId || item.length !== 2 || !availableFlags.includes(field || '') || !val[`${flagId}|apply`]) return
        if (!fixes[itemId]) {
          fixes[itemId] = { itemId }
        }
        fixes[itemId][toCamelCase(field)] = val[key]
      })
      payload.value.fixes = Object.values(fixes)
      if (payload.value.fixes.length > 0) {
        Promise.all([
          assignmentSummaryStore.applyValidationErrors(payload.value),
          assignmentSummaryStore.fetchValidationErrors(level.value),
        ])
          .then(() => {
            modalStore.closeModal(modalName)
          })
          .catch(catchErrors)
      } else {
        modalStore.closeModal(modalName)
      }
    }

    watch(assignment, reloadErrors, { deep: true, immediate: true })
    watch(level, reloadErrors)

    registerModals(modalName)

    return {
      items,
      loading,
      headers,
      dataTableErrors,
      serverItemsLength,

      level,
      modalName,
      submit,
      normalizeName,
      getValidationState,
    }
  },
})
</script>
